import ExitToAppIcon from "@mui/icons-material/OutputRounded";
import { Button, Grid, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import type { Maybe } from "@entrilia/shared/types";
import biApi from "../../../../../../../../../api/biApi";
import type { MeasureUnitTable } from "../../../../../../../../../api/types/svTypes.ts";
import { useDrillDownExport } from "../../../../../hooks/drillDownExport";
import { selectSessionId } from "../../../../../store/selectors/currentReportSelectors";
import type { DrillDownPage } from "../../../../../store/types/drillDownPageState.types";
import { getActiveLayer } from "../../../../../store/utils/getActiveLayer.ts";

enum ShowItems {
  All = "All",
  IncludedInCalculation = "IncludedInCalculation",
}

interface Props {
  configuration: DrillDownPage;
  table: MeasureUnitTable;
  showOnlyIncluded: boolean;
  accountingBook: Maybe<string>;
  onShowOnlyIncludedChange: (showOnlyIncluded: boolean) => void;
}

export default function ToolBar({
  configuration,
  table,
  showOnlyIncluded,
  accountingBook,
  onShowOnlyIncludedChange,
}: Props) {
  const sessionId = useSelector(selectSessionId);
  const exporting = useDrillDownExport(biApi.requestChartOfAccountsExport);

  const activeLayer = getActiveLayer(configuration);
  const exportToExcel = useCallback(() => {
    const caption =
      (activeLayer.measure?.config.customLabel || activeLayer.measure?.meta.caption || "") + " Chart Of Accounts";
    exporting.requestExport({
      caption,
      sessionId,
      itemId: activeLayer.drillDownId || "",
      table,
      includedOnly: showOnlyIncluded,
      accountingBook,
    });
  }, [activeLayer.drillDownId, activeLayer.measure, exporting, sessionId, showOnlyIncluded, table, accountingBook]);

  return (
    <Grid container sx={{ alignItems: "center", width: "100%" }}>
      <ToggleButtonGroup
        exclusive
        value={showOnlyIncluded ? ShowItems.IncludedInCalculation : ShowItems.All}
        sx={{ height: 30 }}
        size="small"
        onChange={(_, value) => onShowOnlyIncludedChange(value === ShowItems.IncludedInCalculation)}
      >
        <ToggleButton value={ShowItems.All} sx={{ width: 84 }} size="small">
          All
        </ToggleButton>
        <ToggleButton value={ShowItems.IncludedInCalculation} sx={{ px: 2 }} size="small">
          Included In Calculation
        </ToggleButton>
      </ToggleButtonGroup>
      <HorizontalFill />
      <Button
        variant="outlined"
        color="secondary"
        disabled={exporting.exporting}
        startIcon={<ExitToAppIcon />}
        loading={exporting.exporting}
        onClick={exportToExcel}
      >
        Export
      </Button>
    </Grid>
  );
}
